import React, { useState, useEffect, useCallback } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { API_BASE_URL } from '../services/api';

// Currency formatting helpers
const formatCurrency = (value) => {
  if (value === null || value === undefined) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const formatPercent = (value) => {
  if (value === null || value === undefined) return 'N/A';
  return `${(value * 100).toFixed(1)}%`;
};

// Cost scenario colors - Blue theme
const SCENARIO_COLORS = {
  'Ultra Low Cost': '#10b981',        
  'Low Cost Blended': '#3b82f6',      
  'Mid-Market Standard': '#6366f1',   
  'High Touch Service': '#f59e0b',    
  'Benchmark Median': '#60a5fa',      
  'Flat Fee Provider': '#1e3a8a'      
};

const PlanEstimatorPage = () => {
  // Form state - REMOVED owner_contribution and num_other_participants
  const [formData, setFormData] = useState({
    num_participants: 6,
    other_participant_contribution: 2000,
    employer_match_total: 15000,
    expected_return: 0.07,
    naics_code: null
  });

  // NAICS search state
  const [naicsSearchTerm, setNaicsSearchTerm] = useState('');
  const [naicsResults, setNaicsResults] = useState([]);
  const [naicsSearching, setNaicsSearching] = useState(false);
  const [selectedNaics, setSelectedNaics] = useState(null);

  // Results state
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showResults, setShowResults] = useState(false);

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: field === 'expected_return' ? parseFloat(value) : parseInt(value) || 0
    }));
  };

  // Search NAICS codes
  const searchNaics = useCallback(async () => {
    setNaicsSearching(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/naics/search?search_term=${encodeURIComponent(naicsSearchTerm)}`);
      
      if (!response.ok) {
        throw new Error('Failed to search NAICS codes');
      }
      
      const data = await response.json();
      setNaicsResults(data);
    } catch (err) {
      console.error('Error searching NAICS:', err);
      setError(err.message);
    } finally {
      setNaicsSearching(false);
    }
  }, [naicsSearchTerm]);

  // Auto-search NAICS codes with debounce
  useEffect(() => {
    const delaySearch = setTimeout(() => {
      if (naicsSearchTerm.length >= 3) {
        searchNaics();
      } else {
        setNaicsResults([]);
      }
    }, 300);

    return () => clearTimeout(delaySearch);
  }, [naicsSearchTerm, searchNaics]);

  // Handle NAICS selection
  const selectNaics = (naics) => {
    setSelectedNaics(naics);
    setFormData(prev => ({
      ...prev,
      naics_code: naics.business_code
    }));
    setNaicsResults([]);
    setNaicsSearchTerm('');
  };

  // Submit analysis
  const submitAnalysis = async () => {
    setLoading(true);
    setError(null);

    try {
      // Prepare API payload - Send defaults for removed fields
      const payload = {
        num_participants: formData.num_participants,
        owner_contribution: 0,  // Default value for removed field
        num_other_participants: formData.num_participants,  // Use total participants
        other_participant_contribution: formData.other_participant_contribution,
        employer_match_total: formData.employer_match_total,
        expected_return: formData.expected_return,
        naics_code: formData.naics_code
      };

      const response = await fetch(`${API_BASE_URL}/plan-estimator/analyze`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Analysis failed');
      }

      const data = await response.json();
      setResults(data);
      setShowResults(true);
    } catch (err) {
      console.error('Error submitting analysis:', err);
      setError(err.message);
      setShowResults(false);
    } finally {
      setLoading(false);
    }
  };

  // Calculate totals - UPDATED to remove owner contribution
  const totals = {
    totalParticipants: formData.num_participants,
    totalContributions: formData.other_participant_contribution * formData.num_participants,
    totalAnnualInflow: (formData.other_participant_contribution * formData.num_participants) + formData.employer_match_total,
    avgPerParticipant: formData.num_participants > 0 
      ? ((formData.other_participant_contribution * formData.num_participants) + formData.employer_match_total) / formData.num_participants
      : 0
  };

  // Prepare chart data
  const prepareProjectionChartData = () => {
    if (!results || !results.cost_scenarios) return [];

    const years = [0, 5, 10, 15, 20, 25];
    return years.map(year => {
      const dataPoint = { year };
      results.cost_scenarios.forEach(scenario => {
        const key = year === 0 ? 'starting_balance' : `projections_${year}yr`;
        dataPoint[scenario.structure_name] = scenario[key]?.net_balance || 0;
      });
      return dataPoint;
    });
  };

  return (
    <div style={{ minHeight: '100vh', background: 'white' }}>
      {/* Header */}
      <header style={{
        background: 'white',
        borderBottom: '1px solid #e5e7eb',
        padding: '1rem 0'
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 2.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: '1.5rem', fontWeight: 600, color: '#1e3a8a', letterSpacing: '0.02em' }}>
                Love Your 401k
              </div>
              <div style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.25rem' }}>
                Modern 401(k) plan analysis powered by data
              </div>
            </div>
            <div style={{ display: 'flex', gap: '2.5rem', alignItems: 'center' }}>
              <a href="/" style={{ color: '#6b7280', textDecoration: 'none', fontSize: '0.9375rem' }}>Search Plans</a>
              <a href="/estimator" style={{ color: '#6b7280', textDecoration: 'none', fontSize: '0.9375rem' }}>What Should I Pay for a 401(k)?</a>
              <a href="/faq" style={{ color: '#6b7280', textDecoration: 'none', fontSize: '0.9375rem' }}>FAQ</a>
            </div>
          </div>
        </div>
      </header>

      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '3rem 2.5rem' }}>
        {/* Hero Section */}
        <div style={{
          background: '#eff6ff',
          borderRadius: '12px',
          padding: '3rem',
          marginBottom: '2rem',
          border: '1px solid #dbeafe'
        }}>
          <h1 style={{ fontSize: '2.5rem', fontWeight: 700, color: '#111827', marginBottom: '0.75rem', letterSpacing: '-0.01em' }}>
            401(k) Plan Cost Estimator
          </h1>
          <p style={{ fontSize: '1.125rem', color: '#6b7280' }}>
            See what you should expect to pay for a 401(k) plan based on comparable companies in your industry
          </p>
        </div>

        {/* Input Form */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
          border: '1px solid #e5e7eb',
          padding: '2rem',
          marginBottom: '2rem'
        }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827', marginBottom: '1.5rem' }}>
            Plan Details
          </h2>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.5rem' }}>
            {/* Total Number of Participants - UPDATED LABEL */}
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, color: '#374151', marginBottom: '0.5rem' }}>
                Total Number of Participants Expected to Participate
              </label>
              <input
                type="number"
                value={formData.num_participants}
                onChange={(e) => handleInputChange('num_participants', e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  outline: 'none',
                  transition: 'all 0.2s'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#1e3a8a';
                  e.target.style.boxShadow = '0 0 0 3px rgba(30, 58, 138, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.boxShadow = 'none';
                }}
                min="1"
              />
              <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.5rem' }}>
                Total Employees who will contribute to their plan
              </p>
            </div>

            {/* Average Annual Contribution per Participant */}
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, color: '#374151', marginBottom: '0.5rem' }}>
                Average Annual Contribution per Participant
              </label>
              <input
                type="number"
                value={formData.other_participant_contribution}
                onChange={(e) => handleInputChange('other_participant_contribution', e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  outline: 'none',
                  transition: 'all 0.2s'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#1e3a8a';
                  e.target.style.boxShadow = '0 0 0 3px rgba(30, 58, 138, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.boxShadow = 'none';
                }}
                min="0"
              />
              <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.5rem' }}>
                Average employee contribution per year
              </p>
            </div>

            {/* Employer Match */}
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, color: '#374151', marginBottom: '0.5rem' }}>
                Total Annual Employer Match
              </label>
              <input
                type="number"
                value={formData.employer_match_total}
                onChange={(e) => handleInputChange('employer_match_total', e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  outline: 'none',
                  transition: 'all 0.2s'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#1e3a8a';
                  e.target.style.boxShadow = '0 0 0 3px rgba(30, 58, 138, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.boxShadow = 'none';
                }}
                min="0"
              />
              <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.5rem' }}>
                Total company match across all participants
              </p>
            </div>

            {/* Expected Return */}
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, color: '#374151', marginBottom: '0.5rem' }}>
                Expected Annual Return Rate
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.expected_return}
                onChange={(e) => handleInputChange('expected_return', e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  outline: 'none',
                  transition: 'all 0.2s'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#1e3a8a';
                  e.target.style.boxShadow = '0 0 0 3px rgba(30, 58, 138, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.boxShadow = 'none';
                }}
                min="0"
                max="1"
              />
              <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.5rem' }}>
                Default is 7% (0.07)
              </p>
            </div>
          </div>

          {/* Summary of Inputs */}
          <div style={{
            marginTop: '1.5rem',
            padding: '1.5rem',
            background: '#eff6ff',
            borderRadius: '8px',
            border: '1px solid #dbeafe'
          }}>
            <h3 style={{ fontWeight: 600, color: '#1e3a8a', marginBottom: '1rem', fontSize: '0.9375rem' }}>
              Summary
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', fontSize: '0.875rem' }}>
              <div>
                <span style={{ color: '#6b7280' }}>Total Participants:</span>
                <div style={{ fontWeight: 700, color: '#111827', fontSize: '1rem', marginTop: '0.25rem' }}>
                  {totals.totalParticipants}
                </div>
              </div>
              <div>
                <span style={{ color: '#6b7280' }}>Annual Contributions:</span>
                <div style={{ fontWeight: 700, color: '#111827', fontSize: '1rem', marginTop: '0.25rem' }}>
                  {formatCurrency(totals.totalContributions)}
                </div>
              </div>
              <div>
                <span style={{ color: '#6b7280' }}>Total Annual Inflow:</span>
                <div style={{ fontWeight: 700, color: '#111827', fontSize: '1rem', marginTop: '0.25rem' }}>
                  {formatCurrency(totals.totalAnnualInflow)}
                </div>
              </div>
              <div>
                <span style={{ color: '#6b7280' }}>Avg per Participant:</span>
                <div style={{ fontWeight: 700, color: '#111827', fontSize: '1rem', marginTop: '0.25rem' }}>
                  {formatCurrency(totals.avgPerParticipant)}
                </div>
              </div>
            </div>
          </div>

          {/* NAICS Search */}
          <div style={{ marginTop: '2rem' }}>
            <h3 style={{ fontSize: '1.125rem', fontWeight: 600, color: '#111827', marginBottom: '1rem' }}>
              Business Type
            </h3>
            
            {!selectedNaics ? (
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, color: '#374151', marginBottom: '0.5rem' }}>
                  Search for your business type
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    value={naicsSearchTerm}
                    onChange={(e) => setNaicsSearchTerm(e.target.value)}
                    placeholder="Start typing... e.g., 'software', 'restaurant', 'construction'"
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'all 0.2s'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#1e3a8a';
                      e.target.style.boxShadow = '0 0 0 3px rgba(30, 58, 138, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e5e7eb';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                  {naicsSearching && (
                    <div style={{ position: 'absolute', right: '1rem', top: '50%', transform: 'translateY(-50%)' }}>
                      <div style={{
                        width: '20px',
                        height: '20px',
                        border: '2px solid #1e3a8a',
                        borderTopColor: 'transparent',
                        borderRadius: '50%',
                        animation: 'spin 0.6s linear infinite'
                      }}></div>
                    </div>
                  )}
                </div>

                {naicsResults.length > 0 && (
                  <div style={{
                    marginTop: '1rem',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    maxHeight: '24rem',
                    overflowY: 'auto'
                  }}>
                    {naicsResults.map((naics) => (
                      <button
                        key={naics.business_code}
                        onClick={() => selectNaics(naics)}
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          padding: '1rem 1.25rem',
                          borderBottom: '1px solid #e5e7eb',
                          background: 'white',
                          border: 'none',
                          cursor: 'pointer',
                          transition: 'background 0.15s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                      >
                        <div style={{ fontWeight: 600, color: '#111827' }}>
                          {naics.business_description}
                        </div>
                        <div style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.25rem' }}>
                          Code: {naics.business_code}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div style={{
                padding: '1.25rem',
                background: '#eff6ff',
                border: '2px solid #dbeafe',
                borderRadius: '8px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <div style={{ fontWeight: 600, color: '#1e3a8a' }}>
                    {selectedNaics.business_description}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.25rem' }}>
                    NAICS Code: {selectedNaics.business_code}
                  </div>
                </div>
                <button
                  onClick={() => {
                    setSelectedNaics(null);
                    setFormData(prev => ({ ...prev, naics_code: null }));
                  }}
                  style={{
                    padding: '0.5rem 1rem',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: '#6b7280',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'color 0.15s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = '#111827'}
                  onMouseLeave={(e) => e.currentTarget.style.color = '#6b7280'}
                >
                  Change
                </button>
              </div>
            )}
          </div>

          {/* Submit Button */}
          <div style={{ marginTop: '2rem' }}>
            <button
              onClick={submitAnalysis}
              disabled={loading || !formData.naics_code}
              style={{
                width: '100%',
                padding: '1rem 1.5rem',
                background: loading || !formData.naics_code ? '#9ca3af' : '#1e3a8a',
                color: 'white',
                fontSize: '1.125rem',
                fontWeight: 600,
                borderRadius: '8px',
                border: 'none',
                cursor: loading || !formData.naics_code ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
                boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)'
              }}
              onMouseEnter={(e) => {
                if (!loading && formData.naics_code) {
                  e.currentTarget.style.background = '#1e40af';
                }
              }}
              onMouseLeave={(e) => {
                if (!loading && formData.naics_code) {
                  e.currentTarget.style.background = '#1e3a8a';
                }
              }}
            >
              {loading ? 'Analyzing...' : 'Analyze Plan Costs'}
            </button>
          </div>

          {error && (
            <div style={{
              marginTop: '1rem',
              padding: '1rem',
              background: '#fee2e2',
              border: '2px solid #fecaca',
              borderRadius: '8px',
              color: '#991b1b',
              fontWeight: 500
            }}>
              {error}
            </div>
          )}
        </div>

        {/* Results Section */}
        {showResults && results && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
            {/* Comparable Plans Statistics */}
            <div style={{
              background: 'white',
              borderRadius: '12px',
              boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
              border: '1px solid #e5e7eb',
              padding: '2rem'
            }}>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827', marginBottom: '1.5rem' }}>
                Comparable Plan Costs
              </h2>
              
              <div style={{
                marginBottom: '1.5rem',
                padding: '1.25rem',
                background: '#eff6ff',
                borderRadius: '8px',
                border: '1px solid #dbeafe'
              }}>
                <p style={{ color: '#374151', fontSize: '0.9375rem' }}>
                  Based on <strong style={{ color: '#1e3a8a' }}>{results.comparable_stats.comparable_plan_count}</strong> comparable plans 
                  in <strong style={{ color: '#1e3a8a' }}>{results.comparable_stats.naics_description}</strong> 
                  {' '}with similar size and contribution patterns.
                </p>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1.5rem' }}>
                <div style={{
                  padding: '1.25rem',
                  background: '#fafbfc',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb'
                }}>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem', fontWeight: 500 }}>
                    Average Admin Fees
                  </div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827' }}>
                    {formatCurrency(results.comparable_stats.avg_admin_fee_per_participant)}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#374151', marginTop: '0.25rem' }}>
                    per participant per year
                  </div>
                </div>

                <div style={{
                  padding: '1.25rem',
                  background: '#fafbfc',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb'
                }}>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem', fontWeight: 500 }}>
                    Average Participant Contribution
                  </div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827' }}>
                    {formatCurrency(formData.other_participant_contribution)}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#374151', marginTop: '0.25rem' }}>
                    per participant per year
                  </div>
                </div>

                <div style={{
                  padding: '1.25rem',
                  background: '#fafbfc',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb'
                }}>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem', fontWeight: 500 }}>
                    Average Employer Match
                  </div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827' }}>
                    {formatCurrency(results.comparable_stats.avg_employer_match_per_participant)}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#374151', marginTop: '0.25rem' }}>
                    per participant per year
                  </div>
                </div>

                <div style={{
                  padding: '1.25rem',
                  background: '#eff6ff',
                  borderRadius: '8px',
                  border: '1px solid #dbeafe'
                }}>
                  <div style={{ fontSize: '0.875rem', color: '#1e3a8a', marginBottom: '0.5rem', fontWeight: 500 }}>
                    Average Total Contribution
                  </div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1e3a8a' }}>
                    {formatCurrency(formData.other_participant_contribution + results.comparable_stats.avg_employer_match_per_participant)}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#1e3a8a', marginTop: '0.25rem' }}>
                    per participant per year
                  </div>
                </div>
              </div>
            </div>

            {/* Projection Chart */}
            <div style={{
              background: 'white',
              borderRadius: '12px',
              boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
              border: '1px solid #e5e7eb',
              padding: '2rem'
            }}>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827', marginBottom: '1.5rem' }}>
                Plan Balance Projections by Fee Structure
              </h2>
              
              <ResponsiveContainer width="100%" height={500}>
                <LineChart data={prepareProjectionChartData()}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis 
                    dataKey="year" 
                    label={{ value: 'Years', position: 'insideBottom', offset: -5 }}
                    stroke="#6B7280"
                  />
                  <YAxis 
                    tickFormatter={(value) => formatCurrency(value)}
                    label={{ value: 'Plan Balance', angle: -90, position: 'insideLeft' }}
                    stroke="#6B7280"
                  />
                  <Tooltip 
                    formatter={(value) => formatCurrency(value)}
                    contentStyle={{ backgroundColor: 'white', border: '2px solid #E5E7EB', borderRadius: '12px' }}
                  />
                  <Legend />
                  {results.cost_scenarios.map((scenario) => (
                    <Line
                      key={scenario.structure_code}
                      type="monotone"
                      dataKey={scenario.structure_name}
                      stroke={SCENARIO_COLORS[scenario.structure_name] || '#888'}
                      strokeWidth={3}
                      dot={{ r: 5 }}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>

              <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '1.5rem', lineHeight: 1.6 }}>
                This chart shows how your plan balance would grow over time under different fee structures, 
                assuming {formatPercent(formData.expected_return)} annual returns and {formatCurrency(totals.totalAnnualInflow)} in 
                total annual contributions and matching.
              </p>
            </div>

            {/* Call to Action */}
            <div style={{
              background: 'linear-gradient(to right, #1e3a8a, #1e40af)',
              borderRadius: '12px',
              padding: '3rem',
              textAlign: 'center',
              color: 'white',
              boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)'
            }}>
              <h2 style={{ fontSize: '2rem', fontWeight: 700, marginBottom: '1rem' }}>
                Ready to Start Your 401(k)?
              </h2>
              <p style={{ fontSize: '1.25rem', marginBottom: '2rem', opacity: 0.9 }}>
                Don't let high fees eat away at your retirement savings. Let us help you find the right provider.
              </p>
              <button style={{
                padding: '1rem 2rem',
                background: 'white',
                color: '#1e3a8a',
                fontWeight: 600,
                borderRadius: '8px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '1rem',
                boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
              >
                Contact Us for More Information
              </button>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default PlanEstimatorPage;
